import { useState, useMemo } from "react";
import { COUNTRY_CODE_TO_NAME } from "../../components/MarketOverviewPage/data/constants";
import { foldSearch } from "../../utils/searchFold";

/**
 * Derive market groups from the existing users array.
 * Each user row appears in every market listed in row.markets.
 * Rows with markets: ["All Markets"] (super admins loaded from API) are
 * expanded to all available codes so they appear in every market group.
 * Returns [[marketName, rows[], roleStats], ...] sorted A-Z.
 */
const computeMarketGroups = (users, searchTerm, allAvailableCodes) => {
  if (!users || users.length === 0) return [];

  // Filter by search term
  const query = foldSearch(searchTerm);
  const filtered = searchTerm
    ? users.filter(
        (u) =>
          foldSearch(u.name).includes(query) ||
          foldSearch(u.email).includes(query)
      )
    : users;

  // Group rows by market code
  const marketMap = {};
  filtered.forEach((row) => {
    let markets = Array.isArray(row.markets) ? row.markets : [];

    // Super admins loaded from API have markets: ["All Markets"] — expand to all codes
    if (
      markets.length === 1 &&
      markets[0] === "All Markets" &&
      allAvailableCodes &&
      allAvailableCodes.length > 0
    ) {
      markets = allAvailableCodes;
    }

    markets.forEach((code) => {
      if (!code || code === "All Markets") return;
      if (!marketMap[code]) marketMap[code] = [];
      marketMap[code].push(row);
    });
  });

  // Build [marketName, rows, roleStats] tuples
  return Object.entries(marketMap)
    .map(([code, rows]) => {
      const marketName = COUNTRY_CODE_TO_NAME[code] || code;

      // Count unique users by role for the header tags
      const seen = new Map();
      rows.forEach((r) => {
        if (!seen.has(r.userId)) seen.set(r.userId, r);
      });

      // Bucket by backend role value; tolerate a stray legacy "super user" form.
      const normalizeRole = (roleName) => {
        const r = (roleName || "").toLowerCase().replace(/[\s-]+/g, "_");
        if (r === "super_admin" || r === "super_user") return "super_admin";
        if (r === "admin") return "admin";
        return "normal_user";
      };

      const roleStats = { super_admin: 0, admin: 0, normal_user: 0 };
      seen.forEach((u) => {
        roleStats[normalizeRole(u.roleName)]++;
      });

      return [marketName, rows, roleStats];
    })
    .sort(([a], [b]) => a.localeCompare(b));
};

export const useMarketView = (users, searchTerm, allAvailableCodes) => {
  const [expandedMarkets, setExpandedMarkets] = useState(new Set());

  // Recomputes automatically whenever users, searchTerm, or available codes change
  const marketGroups = useMemo(
    () => computeMarketGroups(users, searchTerm, allAvailableCodes),
    [users, searchTerm, allAvailableCodes]
  );

  const handleMarketToggle = (marketName) => {
    setExpandedMarkets((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(marketName)) {
        newSet.delete(marketName);
      } else {
        newSet.add(marketName);
      }
      return newSet;
    });
  };

  const expandAllMarkets = () => {
    setExpandedMarkets(new Set(marketGroups.map(([name]) => name)));
  };

  const collapseAllMarkets = () => {
    setExpandedMarkets(new Set());
  };

  return {
    marketGroups,
    expandedMarkets,
    handleMarketToggle,
    expandAllMarkets,
    collapseAllMarkets,
    loading: false,
    contextHolder: null,
  };
};
